import type { ReactNode } from 'react'
import { useLocation } from 'react-router'
import type { InterpolationFunction } from 'styled-components'
import styled from 'styled-components'

import { SideNavigation, TopNavigation, useSideNavigation } from '@loadsmart/loadsmart-ui'
import { Layout, Text } from '@loadsmart/miranda-react'

import user from 'legacy/utils/user'

import { useConfigContext } from 'config/ui/ConfigContext'
import RateCommandCenterHeader from 'rate-command-center/ui/RateCommandCenterHeader'

import shipperGuideDarkLogo from '../../assets/shipperguide-logo-dark.svg'
import shipperGuideDefaultLogo from '../../assets/shipperguide-logo.svg'
import {
  StyledMainWrapper,
  StyledPageWIthSideNav,
  StyledSideNavigationWrapper,
  StyledTopNavigationWrapper,
} from './styles'

function SideNavCollapseWrapper({
  open,
  children,
}: {
  readonly open: boolean
  readonly children: ReactNode
}) {
  return <StyledSideNavigationWrapper $open={open}>{children}</StyledSideNavigationWrapper>
}

function TopNavCollapseWrapper({ children }: { readonly children: ReactNode }) {
  return <StyledTopNavigationWrapper>{children}</StyledTopNavigationWrapper>
}

interface PageWithSideNavProps {
  readonly children: ReactNode
  readonly color?: string
  readonly padding?: string | InterpolationFunction<any>
}

function NavigationLogo({ navPosition }: { readonly navPosition: 'side' | 'top' }) {
  function imgSource() {
    if (navPosition === 'side') {
      return shipperGuideDefaultLogo
    }

    return shipperGuideDarkLogo
  }

  return <img src={imgSource()} width={150} alt="ShipperGuide" />
}

function isActivePath(path: string, pathname: string) {
  return pathname.startsWith(path)
}

const Wrapper = styled.div`
  m-page-content > m-header {
    max-width: 1280px;
    margin-left: auto;
    margin-right: auto;
  }
`

function ShipperGuideLayout({ children, color, padding }: PageWithSideNavProps) {
  const { open, hide, toggle } = useSideNavigation()
  const { enableShipperListPage } = useConfigContext()

  const { pathname } = useLocation()

  if (user.isLoggedIn() === false) {
    return (
      <>
        <RateCommandCenterHeader showAllInfo={false} />
        <Wrapper>{children}</Wrapper>
      </>
    )
  }

  return (
    <StyledPageWIthSideNav>
      <SideNavCollapseWrapper open={open}>
        <SideNavigation>
          <SideNavigation.CloseButton size={24} onClick={hide} />
          <SideNavigation.Logo url="/#/all-in-one/shipments/">
            <NavigationLogo navPosition="side" />
          </SideNavigation.Logo>

          <SideNavigation.Menu>
            <SideNavigation.Menu.Item
              active={isActivePath('/all-in-one/shipment', pathname)}
              label="Shipments"
              url="/#/all-in-one/shipments/"
            />
            <SideNavigation.Menu.Item
              active={isActivePath('/all-in-one/bids', pathname)}
              label="Bids & Tenders"
              url="/#/all-in-one/bids-list/"
            />
            <SideNavigation.Menu.Item
              active={isActivePath('/all-in-one/rfp', pathname)}
              label="RFPs"
              url="/#/all-in-one/rfp/"
            />
            {enableShipperListPage.toBoolean() && (
              <SideNavigation.Menu label="manage">
                <SideNavigation.Menu.Item
                  active={isActivePath('/all-in-one/shippers', pathname)}
                  label="Shippers"
                  url="/#/all-in-one/shippers/"
                />
              </SideNavigation.Menu>
            )}
          </SideNavigation.Menu>
          {process.env.REACT_APP_RELEASE_VERSION && (
            <Layout.Box
              padding="none"
              paddingL="spacing-6"
              paddingB="spacing-4"
              style={{ marginTop: 'auto' }}
            >
              <Text as="p" variant="body-sm" color="color-text-tertiary">
                v{process.env.REACT_APP_RELEASE_VERSION}
              </Text>
            </Layout.Box>
          )}
        </SideNavigation>
      </SideNavCollapseWrapper>
      <StyledMainWrapper color={color} padding={padding}>
        <TopNavCollapseWrapper>
          <TopNavigation>
            <TopNavigation.OpenSideNavButton onClick={toggle} />
            <TopNavigation.Logo url="/#/all-in-one/shipments">
              <NavigationLogo navPosition="top" />
            </TopNavigation.Logo>
            <TopNavigation.Menu align="right">
              <TopNavigation.Menu.Item label={user.email() ?? ''}>
                <TopNavigation.Menu.SubItem
                  label="Logout"
                  onClick={event => {
                    event.preventDefault()
                    user.logout()
                    window.location.href = '/login'
                  }}
                />
              </TopNavigation.Menu.Item>
            </TopNavigation.Menu>
          </TopNavigation>
        </TopNavCollapseWrapper>
        <main>{children}</main>
      </StyledMainWrapper>
    </StyledPageWIthSideNav>
  )
}

export default ShipperGuideLayout
